import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ExperienceModeProvider } from "@/contexts/ExperienceModeContext";
import SiteDegradation from "@/components/SiteDegradation";
import SkipLink from "@/components/SkipLink";
import MicroFeedback from "@/components/MicroFeedback";
import BehaviorReflection from "@/components/BehaviorReflection";
import TimeMoodOverlay from "@/components/TimeMoodOverlay";
import EasterEggOverlay from "@/components/EasterEggOverlay";
import ThemeEffects from "@/components/ThemeEffects";
import SiteMoodOverlay from "@/components/SiteMoodOverlay";
import HiddenCornerTriggers from "@/components/HiddenCornerTriggers";
import AlternateModeOverlay from "@/components/AlternateModeOverlay";
import AlternateModeDiscovery from "@/components/AlternateModeDiscovery";
import UpdateToast from "@/components/UpdateToast";
import KeyboardShortcutsOverlay from "@/components/KeyboardShortcutsOverlay";
import ReadingModeToggle from "@/components/ReadingModeToggle";
import WeatherOverlay from "@/components/WeatherOverlay";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// Eager load critical pages
import Index from "./pages/Index";
import Hallway from "./pages/Hallway";

// Loading fallback
const RoomLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-muted-foreground/30 animate-pulse">...</div>
  </div>
);

// Lazy load secondary pages
const About = lazy(() => import("./pages/About"));
const Support = lazy(() => import("./pages/Support"));
const NotFound = lazy(() => import("./pages/NotFound"));
const YourStats = lazy(() => import("./pages/YourStats"));
const ConstellationMap = lazy(() => import("./pages/ConstellationMap"));
const Updates = lazy(() => import("./pages/Updates"));
const Settings = lazy(() => import("./pages/Settings"));
const Achievements = lazy(() => import("./pages/Achievements"));
const TranslationNavigator = lazy(() => import("./components/TranslationNavigator"));

// Lazy load rooms - grouped by category for better chunking
// Free Rooms
const HowManyTimesLeft = lazy(() => import("./pages/rooms/HowManyTimesLeft"));
const LifeInWeeks = lazy(() => import("./pages/rooms/LifeInWeeks"));
const DelayButton = lazy(() => import("./pages/rooms/DelayButton"));
const OneHonestSentence = lazy(() => import("./pages/rooms/OneHonestSentence"));
const ThisMoment = lazy(() => import("./pages/rooms/ThisMoment"));
const RealityReceipt = lazy(() => import("./pages/rooms/RealityReceipt"));
const NothingHappens = lazy(() => import("./pages/rooms/NothingHappens"));
const DefaultChoice = lazy(() => import("./pages/rooms/DefaultChoice"));
const ThoughtLoop = lazy(() => import("./pages/rooms/ThoughtLoop"));
const ExpectVsHappens = lazy(() => import("./pages/rooms/ExpectVsHappens"));
const QuestionNeverAnswer = lazy(() => import("./pages/rooms/QuestionNeverAnswer"));
const NotSpecial = lazy(() => import("./pages/rooms/NotSpecial"));
const ShortcutAlwaysTake = lazy(() => import("./pages/rooms/ShortcutAlwaysTake"));
const BlurredSelf = lazy(() => import("./pages/rooms/BlurredSelf"));
const ImageZooming = lazy(() => import("./pages/rooms/ImageZooming"));
const ComfortColor = lazy(() => import("./pages/rooms/ComfortColor"));
const LineYouCrossed = lazy(() => import("./pages/rooms/LineYouCrossed"));
const ReadAloud = lazy(() => import("./pages/rooms/ReadAloud"));
const VoiceWithoutMeaning = lazy(() => import("./pages/rooms/VoiceWithoutMeaning"));

// Premium Rooms
const SentenceYouAvoid = lazy(() => import("./pages/rooms/SentenceYouAvoid"));
const SayItOnce = lazy(() => import("./pages/rooms/SayItOnce"));
const ListenToYourself = lazy(() => import("./pages/rooms/ListenToYourself"));
const LifeAsShapes = lazy(() => import("./pages/rooms/LifeAsShapes"));
const FaceWithoutFeatures = lazy(() => import("./pages/rooms/FaceWithoutFeatures"));
const IfNothingChanges = lazy(() => import("./pages/rooms/IfNothingChanges"));
const RealPriorities = lazy(() => import("./pages/rooms/RealPriorities"));
const LastConversation = lazy(() => import("./pages/rooms/LastConversation"));
const WhoWithoutJob = lazy(() => import("./pages/rooms/WhoWithoutJob"));
const ComfortZoneScanner = lazy(() => import("./pages/rooms/ComfortZoneScanner"));

// Night Rooms
const WhatKeepsAwake = lazy(() => import("./pages/rooms/WhatKeepsAwake"));
const YouAt317AM = lazy(() => import("./pages/rooms/YouAt317AM"));
const ThoughtThatReturns = lazy(() => import("./pages/rooms/ThoughtThatReturns"));

// Meta Rooms
const RoomYouVisitMost = lazy(() => import("./pages/rooms/RoomYouVisitMost"));
const RoomYouAvoid = lazy(() => import("./pages/rooms/RoomYouAvoid"));
const LeftTooEarly = lazy(() => import("./pages/rooms/LeftTooEarly"));

// Daily Rooms
const DailyQuote = lazy(() => import("./pages/rooms/DailyQuote"));
const DailyJoke = lazy(() => import("./pages/rooms/DailyJoke"));

// Scary Rooms
const TheyreWatching = lazy(() => import("./pages/rooms/TheyreWatching"));
const TheLastVisitor = lazy(() => import("./pages/rooms/TheLastVisitor"));
const DontLookAway = lazy(() => import("./pages/rooms/DontLookAway"));
const TheWhisper = lazy(() => import("./pages/rooms/TheWhisper"));
const YouForgotSomething = lazy(() => import("./pages/rooms/YouForgotSomething"));
const TheMirrorLies = lazy(() => import("./pages/rooms/TheMirrorLies"));
const SomethingFollows = lazy(() => import("./pages/rooms/SomethingFollows"));
const TheCountdown = lazy(() => import("./pages/rooms/TheCountdown"));
const EyesInTheDark = lazy(() => import("./pages/rooms/EyesInTheDark"));
const TheVoice = lazy(() => import("./pages/rooms/TheVoice"));
const ItKnowsYourName = lazy(() => import("./pages/rooms/ItKnowsYourName"));
const TheDoor = lazy(() => import("./pages/rooms/TheDoor"));

// Boss Rooms
const BossRoomFree = lazy(() => import("./pages/rooms/BossRoomFree"));
const BossRoomHorror = lazy(() => import("./pages/rooms/BossRoomHorror"));
const BossRoomPremium = lazy(() => import("./pages/rooms/BossRoomPremium"));

// Behavioral Rooms
const MouseTracker = lazy(() => import("./pages/rooms/MouseTracker"));
const ScrollConfession = lazy(() => import("./pages/rooms/ScrollConfession"));
const WaitingGame = lazy(() => import("./pages/rooms/WaitingGame"));
const DayInPixels = lazy(() => import("./pages/rooms/DayInPixels"));
const MemoryMaze = lazy(() => import("./pages/rooms/MemoryMaze"));
const DecisionShadow = lazy(() => import("./pages/rooms/DecisionShadow"));
const AnonymousMirrors = lazy(() => import("./pages/rooms/AnonymousMirrors"));
const CollectiveRoom = lazy(() => import("./pages/rooms/CollectiveRoom"));

// Secret Rooms
const SecretRoom = lazy(() => import("./pages/rooms/SecretRoom"));
const GlitchRoom = lazy(() => import("./pages/rooms/GlitchRoom"));

// Time-Locked Rooms
const FullMoonRoom = lazy(() => import("./pages/rooms/FullMoonRoom"));
const WeekendRoom = lazy(() => import("./pages/rooms/WeekendRoom"));
const Room317AM = lazy(() => import("./pages/rooms/Room317AM"));

// Hidden Rooms
const IdleRoom = lazy(() => import("./pages/rooms/IdleRoom"));

// Social Rooms
const InviteFriendRoom = lazy(() => import("./pages/rooms/InviteFriendRoom"));

// Seasonal Rooms
const HalloweenRoom = lazy(() => import("./pages/rooms/HalloweenRoom"));
const WinterSolsticeRoom = lazy(() => import("./pages/rooms/WinterSolsticeRoom"));
const NewYearRoom = lazy(() => import("./pages/rooms/NewYearRoom"));
const SpringEquinoxRoom = lazy(() => import("./pages/rooms/SpringEquinoxRoom"));
const SummerSolsticeRoom = lazy(() => import("./pages/rooms/SummerSolsticeRoom"));
const AutumnEquinoxRoom = lazy(() => import("./pages/rooms/AutumnEquinoxRoom"));
const ValentineRoom = lazy(() => import("./pages/rooms/ValentineRoom"));

// Behavioral Dimension Rooms
const TheRefusal = lazy(() => import("./pages/rooms/TheRefusal"));
const TheSilenceCounter = lazy(() => import("./pages/rooms/TheSilenceCounter"));
const TheExitReflection = lazy(() => import("./pages/rooms/TheExitReflection"));
const ParallelYou = lazy(() => import("./pages/rooms/ParallelYou"));
const YourShadow = lazy(() => import("./pages/rooms/YourShadow"));
const UnvisitedRooms = lazy(() => import("./pages/rooms/UnvisitedRooms"));

// Engagement Rooms
const TimeCapsuleRoom = lazy(() => import("./pages/rooms/TimeCapsuleRoom"));
const TheRevisit = lazy(() => import("./pages/rooms/TheRevisit"));
const ContradictionRoom = lazy(() => import("./pages/rooms/ContradictionRoom"));
const ExportJourneyRoom = lazy(() => import("./pages/rooms/ExportJourneyRoom"));
const AnonymousEchoesRoom = lazy(() => import("./pages/rooms/AnonymousEchoesRoom"));
const TheWaitingRoom = lazy(() => import("./pages/rooms/TheWaitingRoom"));

// Easter Egg Rooms
const VoidRoom = lazy(() => import("./pages/rooms/VoidRoom"));
const PatternRoom = lazy(() => import("./pages/rooms/PatternRoom"));

// AI Rooms
const AIReflectionRoom = lazy(() => import("./pages/rooms/AIReflectionRoom"));
const ImagineYourselfRoom = lazy(() => import("./pages/rooms/ImagineYourselfRoom"));
const DreamVisualizationRoom = lazy(() => import("./pages/rooms/DreamVisualizationRoom"));
const FutureSelfRoom = lazy(() => import("./pages/rooms/FutureSelfRoom"));

// Game Rooms
const MemoryPuzzle = lazy(() => import("./pages/rooms/MemoryPuzzle"));
const ShadowChase = lazy(() => import("./pages/rooms/ShadowChase"));
const TimingRoom = lazy(() => import("./pages/rooms/TimingRoom"));
const PatternWhisper = lazy(() => import("./pages/rooms/PatternWhisper"));
const SilencePuzzle = lazy(() => import("./pages/rooms/SilencePuzzle"));
const DecayGame = lazy(() => import("./pages/rooms/DecayGame"));
const ObservationTest = lazy(() => import("./pages/rooms/ObservationTest"));
const BalanceRoom = lazy(() => import("./pages/rooms/BalanceRoom"));
const WordDrift = lazy(() => import("./pages/rooms/WordDrift"));
const ColorEcho = lazy(() => import("./pages/rooms/ColorEcho"));
const MirrorTap = lazy(() => import("./pages/rooms/MirrorTap"));
const MazeOfDoors = lazy(() => import("./pages/rooms/MazeOfDoors"));
const PulseMatch = lazy(() => import("./pages/rooms/PulseMatch"));
const LightPath = lazy(() => import("./pages/rooms/LightPath"));
const SoundMatch = lazy(() => import("./pages/rooms/SoundMatch"));

// Experimental Rooms
const MirrorRoom = lazy(() => import("./pages/rooms/MirrorRoom"));
const EchoRoom = lazy(() => import("./pages/rooms/EchoRoom"));
const PulseRoom = lazy(() => import("./pages/rooms/PulseRoom"));
const VanishingRoom = lazy(() => import("./pages/rooms/VanishingRoom"));
const LetterRoom = lazy(() => import("./pages/rooms/LetterRoom"));
const EmptyRoom = lazy(() => import("./pages/rooms/EmptyRoom"));
const ReverseRoom = lazy(() => import("./pages/rooms/ReverseRoom"));
const WhisperRoom = lazy(() => import("./pages/rooms/WhisperRoom"));
const ShadowRoom = lazy(() => import("./pages/rooms/ShadowRoom"));
const LabyrinthRoom = lazy(() => import("./pages/rooms/LabyrinthRoom"));
const ColorRoom = lazy(() => import("./pages/rooms/ColorRoom"));
const SurrenderRoom = lazy(() => import("./pages/rooms/SurrenderRoom"));
const NoiseRoom = lazy(() => import("./pages/rooms/NoiseRoom"));
const ObserverRoom = lazy(() => import("./pages/rooms/ObserverRoom"));

// Theme-Exclusive Rooms
const AshEmbersRoom = lazy(() => import("./pages/rooms/AshEmbersRoom"));
const TwilightStarsRoom = lazy(() => import("./pages/rooms/TwilightStarsRoom"));
const VoidAbyssRoom = lazy(() => import("./pages/rooms/VoidAbyssRoom"));
const EclipseShadowsRoom = lazy(() => import("./pages/rooms/EclipseShadowsRoom"));
const DawnAwakeningRoom = lazy(() => import("./pages/rooms/DawnAwakeningRoom"));

// Alternate Mode Exclusive Rooms
const TheOtherSide = lazy(() => import("./pages/rooms/TheOtherSide"));
const TheUnraveling = lazy(() => import("./pages/rooms/TheUnraveling"));
const TheEchoRoom = lazy(() => import("./pages/rooms/TheEcho"));

// Phase 1 New Rooms
const LettersNeverSent = lazy(() => import("./pages/rooms/LettersNeverSent"));
const BreathSync = lazy(() => import("./pages/rooms/BreathSync"));
const PersonalTimeline = lazy(() => import("./pages/PersonalTimeline"));

// Phase 2 New Rooms
const EntropyGarden = lazy(() => import("./pages/rooms/EntropyGarden"));
const MemoryPalaceRoom = lazy(() => import("./pages/rooms/MemoryPalace"));

// Phase 3 New Rooms
const MorningRitualRoom = lazy(() => import("./pages/rooms/MorningRitualRoom"));
const EveningRitualRoom = lazy(() => import("./pages/rooms/EveningRitualRoom"));
const WeeklyRitualRoom = lazy(() => import("./pages/rooms/WeeklyRitualRoom"));
const CommunitySubmissions = lazy(() => import("./pages/CommunitySubmissions"));

// Phase 5 Fragment Trading
const FragmentTradeRoom = lazy(() => import("./pages/rooms/FragmentTradeRoom"));
 
 // Phase 7 AI-Powered Experience Rooms
 const RegretMachineRoom = lazy(() => import("./pages/rooms/RegretMachineRoom"));
 const MemoryReconstructionRoom = lazy(() => import("./pages/rooms/MemoryReconstructionRoom"));

 // Phase 8 Anniversary & Milestone Rooms
 const TheArchiveRoom = lazy(() => import("./pages/rooms/TheArchiveRoom"));
 const MirrorOfAllSelvesRoom = lazy(() => import("./pages/rooms/MirrorOfAllSelvesRoom"));
 const TheEndingRoom = lazy(() => import("./pages/rooms/TheEndingRoom"));

 // Phase 9 Fragment Collection
 const FragmentCollection = lazy(() => import("./pages/FragmentCollection"));

 // Phase 11 Social Whispers & Shared Moments
 const WhisperWallRoom = lazy(() => import("./pages/rooms/WhisperWallRoom"));
 const CollectiveMemoryRoom = lazy(() => import("./pages/rooms/CollectiveMemoryRoom"));
 const SharedMomentsRoom = lazy(() => import("./pages/rooms/SharedMomentsRoom"));

 // Phase 15 Guided Journeys
 const GuidedJourneys = lazy(() => import("./pages/GuidedJourneys"));
 const JourneyPlayer = lazy(() => import("./pages/JourneyPlayer"));

// v3.21.0 New Rooms
const MaskYouWear = lazy(() => import("./pages/rooms/MaskYouWear"));
const RecurringDream = lazy(() => import("./pages/rooms/RecurringDream"));
const PromiseYouBroke = lazy(() => import("./pages/rooms/PromiseYouBroke"));
const WordsYouSwallowed = lazy(() => import("./pages/rooms/WordsYouSwallowed"));
const AgeYouFeel = lazy(() => import("./pages/rooms/AgeYouFeel"));
const BiggestLie = lazy(() => import("./pages/rooms/BiggestLie"));
const PersonYouPretendToBe = lazy(() => import("./pages/rooms/PersonYouPretendToBe"));
const WhatYouCollect = lazy(() => import("./pages/rooms/WhatYouCollect"));
const ApologyYouOwe = lazy(() => import("./pages/rooms/ApologyYouOwe"));
const UnfinishedStory = lazy(() => import("./pages/rooms/UnfinishedStory"));
const VersionOthersSee = lazy(() => import("./pages/rooms/VersionOthersSee"));
const SecretTalent = lazy(() => import("./pages/rooms/SecretTalent"));
const FearYouFeed = lazy(() => import("./pages/rooms/FearYouFeed"));
const GuiltyPleasure = lazy(() => import("./pages/rooms/GuiltyPleasure"));
const RuleYouBreak = lazy(() => import("./pages/rooms/RuleYouBreak"));
const ChildhoodPromise = lazy(() => import("./pages/rooms/ChildhoodPromise"));
const ComplimentYouReject = lazy(() => import("./pages/rooms/ComplimentYouReject"));
const AutopilotMode = lazy(() => import("./pages/rooms/AutopilotMode"));
const BoundaryYouNeed = lazy(() => import("./pages/rooms/BoundaryYouNeed"));
const InnerCritic = lazy(() => import("./pages/rooms/InnerCritic"));
const LastSunday = lazy(() => import("./pages/rooms/LastSunday"));
const WastedHours = lazy(() => import("./pages/rooms/WastedHours"));
const YearThatChanged = lazy(() => import("./pages/rooms/YearThatChanged"));
const TomorrowsRegret = lazy(() => import("./pages/rooms/TomorrowsRegret"));
const MorningAutopilot = lazy(() => import("./pages/rooms/MorningAutopilot"));
const SeasonYouDread = lazy(() => import("./pages/rooms/SeasonYouDread"));
const TenYearsAgo = lazy(() => import("./pages/rooms/TenYearsAgo"));
const LastNormalDay = lazy(() => import("./pages/rooms/LastNormalDay"));
const ClockYouIgnore = lazy(() => import("./pages/rooms/ClockYouIgnore"));
const YourDeadline = lazy(() => import("./pages/rooms/YourDeadline"));
const ThePause = lazy(() => import("./pages/rooms/ThePause"));
const StaticRoom = lazy(() => import("./pages/rooms/StaticRoom"));
const GapBetweenThoughts = lazy(() => import("./pages/rooms/GapBetweenThoughts"));
const SitWithDiscomfort = lazy(() => import("./pages/rooms/SitWithDiscomfort"));
const BoringRoom = lazy(() => import("./pages/rooms/BoringRoom"));
const UnplugRoom = lazy(() => import("./pages/rooms/UnplugRoom"));
const LastPage = lazy(() => import("./pages/rooms/LastPage"));
const ScrollingPattern = lazy(() => import("./pages/rooms/ScrollingPattern"));
const TabYouKeepOpen = lazy(() => import("./pages/rooms/TabYouKeepOpen"));
const DigitalShadow = lazy(() => import("./pages/rooms/DigitalShadow"));
const NotificationYouIgnore = lazy(() => import("./pages/rooms/NotificationYouIgnore"));
const TypingSpeed = lazy(() => import("./pages/rooms/TypingSpeed"));
const LinkYouAlmostClicked = lazy(() => import("./pages/rooms/LinkYouAlmostClicked"));
const BookmarkGraveyard = lazy(() => import("./pages/rooms/BookmarkGraveyard"));
const SearchYouDeleted = lazy(() => import("./pages/rooms/SearchYouDeleted"));
const GravityRoom = lazy(() => import("./pages/rooms/GravityRoom"));
const InkBlot = lazy(() => import("./pages/rooms/InkBlot"));
const HallwayOfMirrors = lazy(() => import("./pages/rooms/HallwayOfMirrors"));
const DissolvingText = lazy(() => import("./pages/rooms/DissolvingText"));
const ThePendulum = lazy(() => import("./pages/rooms/ThePendulum"));
const PixelRain = lazy(() => import("./pages/rooms/PixelRain"));
const TheThreshold = lazy(() => import("./pages/rooms/TheThreshold"));

// v3.22.0 New Rooms
const TheFriendYouLost = lazy(() => import("./pages/rooms/TheFriendYouLost"));
const ConversationYouReplay = lazy(() => import("./pages/rooms/ConversationYouReplay"));
const TheThankYou = lazy(() => import("./pages/rooms/TheThankYou"));
const PeopleYouForgot = lazy(() => import("./pages/rooms/PeopleYouForgot"));
const TheAdviceYouGive = lazy(() => import("./pages/rooms/TheAdviceYouGive"));
const WhatOthersDontKnow = lazy(() => import("./pages/rooms/WhatOthersDontKnow"));
const TheLieYouBelieve = lazy(() => import("./pages/rooms/TheLieYouBelieve"));
const YourShadowSelf = lazy(() => import("./pages/rooms/YourShadowSelf"));
const TheStoryYouTell = lazy(() => import("./pages/rooms/TheStoryYouTell"));
const YourSafePlace = lazy(() => import("./pages/rooms/YourSafePlace"));
const TheRiskYouDidntTake = lazy(() => import("./pages/rooms/TheRiskYouDidntTake"));
const YourLastExcuse = lazy(() => import("./pages/rooms/YourLastExcuse"));
const TheHabitYouHide = lazy(() => import("./pages/rooms/TheHabitYouHide"));
const WhatYouPostpone = lazy(() => import("./pages/rooms/WhatYouPostpone"));
const TheDoorYouClosed = lazy(() => import("./pages/rooms/TheDoorYouClosed"));

// v3.22.0 New Pages
const ReflectionJournal = lazy(() => import("./pages/ReflectionJournal"));
const CompanionSetup = lazy(() => import("./pages/CompanionSetup"));
const CompanionChat = lazy(() => import("./pages/CompanionChat"));

const queryClient = new QueryClient();

const App = () => (
  <LanguageProvider>
    <ExperienceModeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SkipLink />
          <SiteDegradation />
          <TimeMoodOverlay />
          <SiteMoodOverlay />
          <AlternateModeOverlay />
          <WeatherOverlay />
          <ThemeEffects />
          <MicroFeedback />
          <BehaviorReflection />
          <EasterEggOverlay />
          <HiddenCornerTriggers />
          <Toaster />
          <Sonner />
          <PWAInstallPrompt />
          <BrowserRouter>
            <AlternateModeDiscovery />
            <UpdateToast />
            <KeyboardShortcutsOverlay />
            <ReadingModeToggle />
            <Suspense fallback={<RoomLoading />}>
              <main id="main-content" tabIndex={-1}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/hallway" element={<Hallway />} />
                <Route path="/about" element={<About />} />
                <Route path="/support" element={<Support />} />
                <Route path="/unlock" element={<Support />} />
                <Route path="/updates" element={<Updates />} />
                <Route path="/constellation" element={<ConstellationMap />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/achievements" element={<Achievements />} />
                <Route path="/translations" element={<TranslationNavigator />} />
                <Route path="/timeline" element={<PersonalTimeline />} />
                 <Route path="/your-stats" element={<YourStats />} />
                <Route path="/stats" element={<YourStats />} />
                <Route path="/collection" element={<FragmentCollection />} />
                
                {/* Free Rooms */}
                <Route path="/room/how-many-times-left" element={<HowManyTimesLeft />} />
                <Route path="/room/life-in-weeks" element={<LifeInWeeks />} />
                <Route path="/room/delay-button" element={<DelayButton />} />
                <Route path="/room/one-honest-sentence" element={<OneHonestSentence />} />
                <Route path="/room/this-moment" element={<ThisMoment />} />
                <Route path="/room/reality-receipt" element={<RealityReceipt />} />
                <Route path="/room/nothing-happens" element={<NothingHappens />} />
                <Route path="/room/default-choice" element={<DefaultChoice />} />
                <Route path="/room/thought-loop" element={<ThoughtLoop />} />
                <Route path="/room/expect-vs-happens" element={<ExpectVsHappens />} />
                <Route path="/room/question-never-answer" element={<QuestionNeverAnswer />} />
                <Route path="/room/not-special" element={<NotSpecial />} />
                <Route path="/room/shortcut-always-take" element={<ShortcutAlwaysTake />} />
                <Route path="/room/blurred-self" element={<BlurredSelf />} />
                <Route path="/room/image-zooming" element={<ImageZooming />} />
                <Route path="/room/comfort-color" element={<ComfortColor />} />
                <Route path="/room/line-you-crossed" element={<LineYouCrossed />} />
                <Route path="/room/read-aloud" element={<ReadAloud />} />
                <Route path="/room/voice-without-meaning" element={<VoiceWithoutMeaning />} />
                
                {/* Premium Rooms */}
                <Route path="/room/sentence-you-avoid" element={<SentenceYouAvoid />} />
                <Route path="/room/say-it-once" element={<SayItOnce />} />
                <Route path="/room/listen-to-yourself" element={<ListenToYourself />} />
                <Route path="/room/life-as-shapes" element={<LifeAsShapes />} />
                <Route path="/room/face-without-features" element={<FaceWithoutFeatures />} />
                <Route path="/room/if-nothing-changes" element={<IfNothingChanges />} />
                <Route path="/room/real-priorities" element={<RealPriorities />} />
                <Route path="/room/last-conversation" element={<LastConversation />} />
                <Route path="/room/who-without-job" element={<WhoWithoutJob />} />
                <Route path="/room/comfort-zone-scanner" element={<ComfortZoneScanner />} />
                
                {/* Night Rooms */}
                <Route path="/room/what-keeps-awake" element={<WhatKeepsAwake />} />
                <Route path="/room/you-at-317am" element={<YouAt317AM />} />
                <Route path="/room/thought-that-returns" element={<ThoughtThatReturns />} />
                
                {/* Meta Rooms */}
                <Route path="/room/room-you-visit-most" element={<RoomYouVisitMost />} />
                <Route path="/room/room-you-avoid" element={<RoomYouAvoid />} />
                <Route path="/room/left-too-early" element={<LeftTooEarly />} />
                
                {/* Daily Rooms */}
                <Route path="/room/daily-quote" element={<DailyQuote />} />
                <Route path="/room/daily-joke" element={<DailyJoke />} />
                
                {/* Scary Rooms */}
                <Route path="/room/theyre-watching" element={<TheyreWatching />} />
                <Route path="/room/the-last-visitor" element={<TheLastVisitor />} />
                <Route path="/room/dont-look-away" element={<DontLookAway />} />
                <Route path="/room/the-whisper" element={<TheWhisper />} />
                <Route path="/room/you-forgot-something" element={<YouForgotSomething />} />
                <Route path="/room/the-mirror-lies" element={<TheMirrorLies />} />
                <Route path="/room/something-follows" element={<SomethingFollows />} />
                <Route path="/room/the-countdown" element={<TheCountdown />} />
                <Route path="/room/eyes-in-the-dark" element={<EyesInTheDark />} />
                <Route path="/room/the-voice" element={<TheVoice />} />
                <Route path="/room/it-knows-your-name" element={<ItKnowsYourName />} />
                <Route path="/room/the-door" element={<TheDoor />} />
                
                {/* Boss Rooms */}
                <Route path="/room/boss-room-free" element={<BossRoomFree />} />
                <Route path="/room/boss-room-horror" element={<BossRoomHorror />} />
                <Route path="/room/boss-room-premium" element={<BossRoomPremium />} />
                
                {/* Behavioral Rooms */}
                <Route path="/room/mouse-tracker" element={<MouseTracker />} />
                <Route path="/room/scroll-confession" element={<ScrollConfession />} />
                <Route path="/room/waiting-game" element={<WaitingGame />} />
                <Route path="/room/day-in-pixels" element={<DayInPixels />} />
                <Route path="/room/memory-maze" element={<MemoryMaze />} />
                <Route path="/room/decision-shadow" element={<DecisionShadow />} />
                <Route path="/room/anonymous-mirrors" element={<AnonymousMirrors />} />
                <Route path="/room/collective-room" element={<CollectiveRoom />} />
                
                {/* Secret Rooms */}
                <Route path="/room/secret-room" element={<SecretRoom />} />
                <Route path="/room/glitch-room" element={<GlitchRoom />} />
                
                {/* Time-Locked Rooms */}
                <Route path="/room/full-moon-room" element={<FullMoonRoom />} />
                <Route path="/room/weekend-room" element={<WeekendRoom />} />
                <Route path="/room/317am-room" element={<Room317AM />} />
                
                {/* Secret Discovery Rooms */}
                <Route path="/room/idle-room" element={<IdleRoom />} />
                
                {/* Social Rooms */}
                <Route path="/room/invite-friend-room" element={<InviteFriendRoom />} />
                
                {/* Seasonal Rooms */}
                <Route path="/room/halloween-room" element={<HalloweenRoom />} />
                <Route path="/room/winter-solstice-room" element={<WinterSolsticeRoom />} />
                <Route path="/room/new-year-room" element={<NewYearRoom />} />
                
                {/* Behavioral Dimension Rooms */}
                <Route path="/room/the-refusal" element={<TheRefusal />} />
                <Route path="/room/silence-counter" element={<TheSilenceCounter />} />
                <Route path="/room/exit-reflection" element={<TheExitReflection />} />
                <Route path="/room/parallel-you" element={<ParallelYou />} />
                <Route path="/room/your-shadow" element={<YourShadow />} />
                <Route path="/room/unvisited-rooms" element={<UnvisitedRooms />} />
                
                {/* Engagement & Depth Rooms */}
                <Route path="/room/time-capsule" element={<TimeCapsuleRoom />} />
                <Route path="/room/the-revisit" element={<TheRevisit />} />
                <Route path="/room/contradiction-room" element={<ContradictionRoom />} />
                <Route path="/room/export-journey" element={<ExportJourneyRoom />} />
                <Route path="/room/anonymous-echoes" element={<AnonymousEchoesRoom />} />
                <Route path="/room/the-waiting-room" element={<TheWaitingRoom />} />
                
                {/* Easter Egg Rooms */}
                <Route path="/room/void-room" element={<VoidRoom />} />
                <Route path="/room/pattern-room" element={<PatternRoom />} />
                
                {/* AI Rooms */}
                <Route path="/room/ai-reflection-room" element={<AIReflectionRoom />} />
                <Route path="/room/imagine-yourself" element={<ImagineYourselfRoom />} />
                <Route path="/room/dream-visualization" element={<DreamVisualizationRoom />} />
                <Route path="/room/future-self" element={<FutureSelfRoom />} />
                
                {/* Game Rooms */}
                <Route path="/room/memory-puzzle" element={<MemoryPuzzle />} />
                <Route path="/room/shadow-chase" element={<ShadowChase />} />
                <Route path="/room/timing-room" element={<TimingRoom />} />
                <Route path="/room/pattern-whisper" element={<PatternWhisper />} />
                <Route path="/room/silence-puzzle" element={<SilencePuzzle />} />
                <Route path="/room/decay-game" element={<DecayGame />} />
                <Route path="/room/observation-test" element={<ObservationTest />} />
                <Route path="/room/balance-room" element={<BalanceRoom />} />
                <Route path="/room/word-drift" element={<WordDrift />} />
                <Route path="/room/color-echo" element={<ColorEcho />} />
                <Route path="/room/mirror-tap" element={<MirrorTap />} />
                <Route path="/room/maze-of-doors" element={<MazeOfDoors />} />
                <Route path="/room/pulse-match" element={<PulseMatch />} />
                <Route path="/room/light-path" element={<LightPath />} />
                <Route path="/room/sound-match" element={<SoundMatch />} />
                
                {/* Experimental Rooms */}
                <Route path="/room/mirror-room" element={<MirrorRoom />} />
                <Route path="/room/echo-room" element={<EchoRoom />} />
                <Route path="/room/pulse-room" element={<PulseRoom />} />
                <Route path="/room/vanishing-room" element={<VanishingRoom />} />
                <Route path="/room/letter-room" element={<LetterRoom />} />
                <Route path="/room/empty-room" element={<EmptyRoom />} />
                <Route path="/room/reverse-room" element={<ReverseRoom />} />
                <Route path="/room/whisper-room" element={<WhisperRoom />} />
                <Route path="/room/shadow-room" element={<ShadowRoom />} />
                <Route path="/room/labyrinth-room" element={<LabyrinthRoom />} />
                <Route path="/room/color-room" element={<ColorRoom />} />
                <Route path="/room/surrender-room" element={<SurrenderRoom />} />
                <Route path="/room/noise-room" element={<NoiseRoom />} />
                <Route path="/room/observer-room" element={<ObserverRoom />} />
                
                {/* Theme-Exclusive Rooms */}
                <Route path="/room/ash-embers" element={<AshEmbersRoom />} />
                <Route path="/room/twilight-stars" element={<TwilightStarsRoom />} />
                <Route path="/room/void-abyss" element={<VoidAbyssRoom />} />
                <Route path="/room/eclipse-shadows" element={<EclipseShadowsRoom />} />
                <Route path="/room/dawn-awakening" element={<DawnAwakeningRoom />} />
                
                {/* Alternate Mode Exclusive Rooms */}
                <Route path="/room/the-other-side" element={<TheOtherSide />} />
                <Route path="/room/the-unraveling" element={<TheUnraveling />} />
                <Route path="/room/the-echo" element={<TheEchoRoom />} />
                
                {/* Phase 1 New Rooms */}
                <Route path="/room/letters-never-sent" element={<LettersNeverSent />} />
                <Route path="/room/breath-sync" element={<BreathSync />} />
                
                {/* Phase 2 New Rooms */}
                <Route path="/room/entropy-garden" element={<EntropyGarden />} />
                <Route path="/room/memory-palace" element={<MemoryPalaceRoom />} />
                
                {/* Phase 3 New Rooms */}
                <Route path="/room/morning-ritual" element={<MorningRitualRoom />} />
                <Route path="/room/evening-ritual" element={<EveningRitualRoom />} />
                <Route path="/room/weekly-ritual" element={<WeeklyRitualRoom />} />
                <Route path="/community" element={<CommunitySubmissions />} />
                
                {/* Phase 4 Seasonal Rooms */}
                <Route path="/room/spring-equinox" element={<SpringEquinoxRoom />} />
                <Route path="/room/summer-solstice" element={<SummerSolsticeRoom />} />
                <Route path="/room/autumn-equinox" element={<AutumnEquinoxRoom />} />
                <Route path="/room/valentine" element={<ValentineRoom />} />
                
                {/* Phase 5 Fragment Trading */}
                <Route path="/room/fragment-trade" element={<FragmentTradeRoom />} />
                 
                 {/* Phase 7 AI-Powered Experience Rooms */}
                 <Route path="/room/regret-machine" element={<RegretMachineRoom />} />
                 <Route path="/room/memory-reconstruction" element={<MemoryReconstructionRoom />} />
                 
                 {/* Phase 8 Anniversary & Milestone Rooms */}
                 <Route path="/room/the-archive" element={<TheArchiveRoom />} />
                 <Route path="/room/mirror-of-all-selves" element={<MirrorOfAllSelvesRoom />} />
                 <Route path="/room/the-ending" element={<TheEndingRoom />} />
                 
                 {/* Phase 11 Social Whispers & Shared Moments */}
                 <Route path="/room/whisper-wall" element={<WhisperWallRoom />} />
                 <Route path="/room/collective-memory" element={<CollectiveMemoryRoom />} />
                 <Route path="/room/shared-moments" element={<SharedMomentsRoom />} />

                 {/* Phase 15 Guided Journeys */}
                 <Route path="/journeys" element={<GuidedJourneys />} />
                 <Route path="/journey/:journeyId" element={<JourneyPlayer />} />

                 {/* v3.21.0 New Rooms */}
                 <Route path="/room/mask-you-wear" element={<MaskYouWear />} />
                 <Route path="/room/recurring-dream" element={<RecurringDream />} />
                 <Route path="/room/promise-you-broke" element={<PromiseYouBroke />} />
                 <Route path="/room/words-you-swallowed" element={<WordsYouSwallowed />} />
                 <Route path="/room/age-you-feel" element={<AgeYouFeel />} />
                 <Route path="/room/biggest-lie" element={<BiggestLie />} />
                 <Route path="/room/person-you-pretend" element={<PersonYouPretendToBe />} />
                 <Route path="/room/what-you-collect" element={<WhatYouCollect />} />
                 <Route path="/room/apology-you-owe" element={<ApologyYouOwe />} />
                 <Route path="/room/unfinished-story" element={<UnfinishedStory />} />
                 <Route path="/room/version-others-see" element={<VersionOthersSee />} />
                 <Route path="/room/secret-talent" element={<SecretTalent />} />
                 <Route path="/room/fear-you-feed" element={<FearYouFeed />} />
                 <Route path="/room/guilty-pleasure" element={<GuiltyPleasure />} />
                 <Route path="/room/rule-you-break" element={<RuleYouBreak />} />
                 <Route path="/room/childhood-promise" element={<ChildhoodPromise />} />
                 <Route path="/room/compliment-you-reject" element={<ComplimentYouReject />} />
                 <Route path="/room/autopilot-mode" element={<AutopilotMode />} />
                 <Route path="/room/boundary-you-need" element={<BoundaryYouNeed />} />
                 <Route path="/room/inner-critic" element={<InnerCritic />} />
                 <Route path="/room/last-sunday" element={<LastSunday />} />
                 <Route path="/room/wasted-hours" element={<WastedHours />} />
                 <Route path="/room/year-that-changed" element={<YearThatChanged />} />
                 <Route path="/room/tomorrows-regret" element={<TomorrowsRegret />} />
                 <Route path="/room/morning-autopilot" element={<MorningAutopilot />} />
                 <Route path="/room/season-you-dread" element={<SeasonYouDread />} />
                 <Route path="/room/ten-years-ago" element={<TenYearsAgo />} />
                 <Route path="/room/last-normal-day" element={<LastNormalDay />} />
                 <Route path="/room/clock-you-ignore" element={<ClockYouIgnore />} />
                 <Route path="/room/your-deadline" element={<YourDeadline />} />
                 <Route path="/room/the-pause" element={<ThePause />} />
                 <Route path="/room/static-room" element={<StaticRoom />} />
                 <Route path="/room/gap-between-thoughts" element={<GapBetweenThoughts />} />
                 <Route path="/room/sit-with-discomfort" element={<SitWithDiscomfort />} />
                 <Route path="/room/boring-room" element={<BoringRoom />} />
                 <Route path="/room/unplug" element={<UnplugRoom />} />
                 <Route path="/room/last-page" element={<LastPage />} />
                 <Route path="/room/scrolling-pattern" element={<ScrollingPattern />} />
                 <Route path="/room/tab-you-keep-open" element={<TabYouKeepOpen />} />
                 <Route path="/room/digital-shadow" element={<DigitalShadow />} />
                 <Route path="/room/notification-you-ignore" element={<NotificationYouIgnore />} />
                 <Route path="/room/typing-speed" element={<TypingSpeed />} />
                 <Route path="/room/link-you-almost-clicked" element={<LinkYouAlmostClicked />} />
                 <Route path="/room/bookmark-graveyard" element={<BookmarkGraveyard />} />
                 <Route path="/room/search-you-deleted" element={<SearchYouDeleted />} />
                 <Route path="/room/gravity-room" element={<GravityRoom />} />
                 <Route path="/room/ink-blot" element={<InkBlot />} />
                 <Route path="/room/hallway-of-mirrors" element={<HallwayOfMirrors />} />
                 <Route path="/room/dissolving-text" element={<DissolvingText />} />
                 <Route path="/room/the-pendulum" element={<ThePendulum />} />
                 <Route path="/room/pixel-rain" element={<PixelRain />} />
                  <Route path="/room/the-threshold" element={<TheThreshold />} />

                 {/* v3.22.0 New Rooms */}
                 <Route path="/room/the-friend-you-lost" element={<TheFriendYouLost />} />
                 <Route path="/room/conversation-you-replay" element={<ConversationYouReplay />} />
                 <Route path="/room/the-thank-you" element={<TheThankYou />} />
                 <Route path="/room/people-you-forgot" element={<PeopleYouForgot />} />
                 <Route path="/room/the-advice-you-give" element={<TheAdviceYouGive />} />
                 <Route path="/room/what-others-dont-know" element={<WhatOthersDontKnow />} />
                 <Route path="/room/the-lie-you-believe" element={<TheLieYouBelieve />} />
                 <Route path="/room/your-shadow-self" element={<YourShadowSelf />} />
                 <Route path="/room/the-story-you-tell" element={<TheStoryYouTell />} />
                 <Route path="/room/your-safe-place" element={<YourSafePlace />} />
                 <Route path="/room/the-risk-you-didnt-take" element={<TheRiskYouDidntTake />} />
                 <Route path="/room/your-last-excuse" element={<YourLastExcuse />} />
                 <Route path="/room/the-habit-you-hide" element={<TheHabitYouHide />} />
                 <Route path="/room/what-you-postpone" element={<WhatYouPostpone />} />
                 <Route path="/room/the-door-you-closed" element={<TheDoorYouClosed />} />

                 {/* v3.22.0 New Pages */}
                 <Route path="/journal" element={<ReflectionJournal />} />
                 <Route path="/companion/setup" element={<CompanionSetup />} />
                 <Route path="/companion" element={<CompanionChat />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ExperienceModeProvider>
</LanguageProvider>
);

export default App;
